'use client'

/**
 * §19.10.1 rule 4 — "a different file asks first." Native <dialog> for
 * built-in focus trapping, Esc-to-close and top-layer stacking without
 * pulling in a new primitive outside components/create/**.
 */

import { useEffect, useRef } from 'react'

interface FileMismatchModalProps {
  open: boolean
  previousFileName: string
  newFileName: string
  onReapply: () => void
  onDiscard: () => void
}

export function FileMismatchModal({ open, previousFileName, newFileName, onReapply, onDiscard }: FileMismatchModalProps) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  return (
    <dialog
      ref={ref}
      aria-labelledby="file-mismatch-title"
      className="m-auto max-w-md border border-[color:var(--tls-border-strong)] bg-[var(--tls-surface)] p-0 backdrop:bg-black/50"
      onCancel={(e) => { e.preventDefault(); onDiscard() }}
    >
      <div className="p-6">
        <h2 id="file-mismatch-title" className="text-base font-semibold text-[var(--tls-fg-strong)]">
          This is a different file
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-[var(--tls-fg-muted)]">
          Your waiting edits were made against <span className="font-medium text-[var(--tls-fg-strong)]">{previousFileName}</span>.
          You just dropped <span className="font-medium text-[var(--tls-fg-strong)]">{newFileName}</span>. Applying trim
          points from one file to a different one can cut it somewhere that makes no sense, so this needs a choice
          rather than happening silently.
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onReapply}
            className="tlsr-focus min-h-[44px] bg-[var(--tls-brand)] px-5 text-sm font-semibold text-[var(--tls-brand-fg)]"
          >
            Re-apply your edits
          </button>
          <button
            type="button"
            onClick={onDiscard}
            className="tlsr-focus min-h-[44px] border border-[color:var(--tls-border-strong)] px-5 text-sm font-medium text-[var(--tls-fg-strong)]"
          >
            Discard and start clean
          </button>
        </div>
      </div>
    </dialog>
  )
}
